import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  VStack,
  Text,
  Spinner,
  InputGroup,
  InputLeftAddon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { AddIcon } from '@chakra-ui/icons';
import { FaEllipsisV } from 'react-icons/fa';

interface Banco {
  id: string;
  nome: string;
  saldo_inicial: number;
  saldo_data?: string | null;
  saldo_conferido_em?: string | null;
  criado_em: string;
  // saldo_atual será calculado no frontend
  saldo_atual?: number;
}

const formatarMoedaInput = (valor: string | number): string => {
  let v = typeof valor === 'number' ? Math.round(valor * 100).toString() : valor.replace(/\D/g, '');
  if (!v) return '0,00';
  while (v.length < 3) v = '0' + v;
  const centavos = v.slice(-2);
  let reais = v.slice(0, -2);
  reais = reais.replace(/^0+/, '') || '0';
  reais = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${reais},${centavos}`;
};

const moedaParaNumero = (valor: string): number => {
  return Number(valor.replace(/\./g, '').replace(',', '.'));
};

const Bancos: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [bancos, setBancos] = useState<Banco[]>([]);
  const [nomeBanco, setNomeBanco] = useState('');
  const [saldoInicial, setSaldoInicial] = useState('0,00');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSaldos, setLoadingSaldos] = useState<boolean>(false);
  // Ajuste de saldo: o usuário informa o saldo REAL de hoje e o sistema
  // recalcula o saldo inicial para o saldo atual bater com a realidade
  const [ajusteBanco, setAjusteBanco] = useState<Banco | null>(null);
  const [ajusteNome, setAjusteNome] = useState('');
  const [ajusteSaldo, setAjusteSaldo] = useState('0,00');
  const [excluirBanco, setExcluirBanco] = useState<Banco | null>(null);
  const cancelRef = React.useRef<any>(null);
  const cancelExcluirRef = React.useRef<any>(null);
  const novo = useDisclosure();

  const handleExcluirBanco = async () => {
    if (!excluirBanco) return;
    setLoading(true);
    try {
      await api.delete(`/api/bancos/${excluirBanco.id}`);
      toast({ title: 'Banco excluído!', status: 'success' });
      setExcluirBanco(null);
      fetchBancosEAtualizarSaldos();
    } catch (err) {
      // O servidor bloqueia exclusão de banco com lançamentos ou contas
      // vinculados (409), pra não deixar o histórico órfão
      toast({
        title: 'Não foi possível excluir',
        status: 'error',
        description: err instanceof Error ? err.message : undefined,
        duration: 8000,
      });
    }
    setLoading(false);
  };

  const fetchBancosEAtualizarSaldos = async () => {
    if (!user) return;
    setLoading(true);
    setLoadingSaldos(true);
    try {
      // O saldo atual de cada banco já vem calculado pelo servidor
      const bancosData = await api.get('/api/bancos');
      setBancos(bancosData as Banco[]);
    } catch (err) {
      toast({ title: 'Erro ao buscar bancos', status: 'error', description: err instanceof Error ? err.message : undefined });
    }
    setLoadingSaldos(false);
    setLoading(false);
  };

  useEffect(() => {
    if (user) {
        fetchBancosEAtualizarSaldos();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleAddBanco = async () => {
    if (!user || !nomeBanco.trim()) {
      toast({ title: 'Nome do banco é obrigatório', status: 'warning' });
      return;
    }
    const saldoInicialNum = moedaParaNumero(saldoInicial);
    if (isNaN(saldoInicialNum)) {
        toast({ title: 'Saldo inicial inválido', status: 'error' });
        return;
    }

    setLoading(true);
    try {
      await api.post('/api/bancos', { nome: nomeBanco, saldo_inicial: saldoInicialNum });
      setNomeBanco('');
      setSaldoInicial('0,00');
      novo.onClose();
      toast({ title: 'Banco adicionado com sucesso!', status: 'success' });
      fetchBancosEAtualizarSaldos();
    } catch (err) {
      toast({ title: 'Erro ao adicionar banco', status: 'error', description: err instanceof Error ? err.message : undefined });
    }
    setLoading(false);
  };
  
  const handleSaldoInicialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSaldoInicial(formatarMoedaInput(e.target.value));
  };

  const abrirAjuste = (banco: Banco) => {
    setAjusteBanco(banco);
    setAjusteNome(banco.nome);
    setAjusteSaldo(formatarMoedaInput(banco.saldo_atual ?? banco.saldo_inicial));
  };

  const handleAjustarBanco = async () => {
    if (!ajusteBanco) return;
    if (!ajusteNome.trim()) {
      toast({ title: 'Nome do banco é obrigatório', status: 'warning' });
      return;
    }
    const saldoReal = moedaParaNumero(ajusteSaldo);
    if (isNaN(saldoReal)) {
      toast({ title: 'Saldo inválido', status: 'error' });
      return;
    }
    setLoading(true);
    try {
      // O saldo do app do banco vira a referência do fim de hoje: só o que
      // for lançado com data depois disso mexe nele
      await api.put(`/api/bancos/${ajusteBanco.id}`, {
        nome: ajusteNome.trim(),
        saldo_real: saldoReal,
      });
      toast({ title: 'Banco atualizado!', status: 'success' });
      setAjusteBanco(null);
      fetchBancosEAtualizarSaldos();
    } catch (err) {
      toast({ title: 'Erro ao atualizar banco', status: 'error', description: err instanceof Error ? err.message : undefined });
    }
    setLoading(false);
  };

  if (!user) {
    return <Spinner />;
  }

  const total = bancos.reduce((acc, b) => acc + Number(b.saldo_atual ?? b.saldo_inicial), 0);

  return (
    <Box p={4} maxW="800px" mx="auto" pt={{ base: 4, md: 8 }}>
      <Flex justify="space-between" align="center" mb={4} gap={2}>
        <Heading size="lg">Bancos</Heading>
        <Button colorScheme="teal" size="sm" leftIcon={<AddIcon />} onClick={novo.onOpen}>Novo banco</Button>
      </Flex>

      {loading && bancos.length === 0 ? (
        <Spinner />
      ) : bancos.length === 0 ? (
        <Box p={6} borderWidth="1px" borderRadius="xl" textAlign="center">
          <Text mb={3}>Nenhum banco cadastrado ainda.</Text>
          <Button colorScheme="teal" onClick={novo.onOpen}>Cadastrar o primeiro</Button>
        </Box>
      ) : (
        <VStack align="stretch" spacing={3}>
          <Flex justify="space-between" align="baseline" px={1}>
            <Text fontSize="sm" color="gray.500">Total em conta</Text>
            <Text fontSize="xl" fontWeight="bold" color={total < 0 ? 'red.500' : 'teal.600'}>R$ {formatarMoedaInput(total)}</Text>
          </Flex>
          {bancos.map((banco) => {
            const atual = Number(banco.saldo_atual ?? banco.saldo_inicial);
            return (
              <Box key={banco.id} p={4} borderWidth="1px" borderColor="gray.100" borderRadius="xl" boxShadow="sm" bg="white">
                <Flex justify="space-between" align="flex-start" gap={2}>
                  <Box minW={0}>
                    <Text fontWeight="semibold" noOfLines={1}>{banco.nome}</Text>
                    <Text fontSize="2xl" fontWeight="bold" color={atual < 0 ? 'red.500' : 'teal.600'} lineHeight="1.2">
                      {loadingSaldos && banco.saldo_atual === undefined ? <Spinner size="sm" /> : `R$ ${formatarMoedaInput(atual)}`}
                    </Text>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      {banco.saldo_conferido_em
                        ? `Conferido com o banco em ${new Date(banco.saldo_conferido_em).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                        : banco.saldo_data
                        ? `Conferido com o banco em ${banco.saldo_data.slice(8, 10)}/${banco.saldo_data.slice(5, 7)}: R$ ${formatarMoedaInput(banco.saldo_inicial)}`
                        : `Saldo inicial: R$ ${formatarMoedaInput(banco.saldo_inicial)}`}
                    </Text>
                  </Box>
                  <Menu placement="bottom-end">
                    <MenuButton as={IconButton} aria-label={`Mais ações de ${banco.nome}`} icon={<Icon as={FaEllipsisV as any} />} variant="ghost" size="sm" />
                    <MenuList>
                      <MenuItem onClick={() => abrirAjuste(banco)}>Renomear</MenuItem>
                      <MenuItem color="red.500" onClick={() => setExcluirBanco(banco)}>Excluir</MenuItem>
                    </MenuList>
                  </Menu>
                </Flex>
                <Flex gap={2} mt={3}>
                  <Button size="sm" flex="1" variant="outline" onClick={() => navigate(`/extrato-banco?banco_id=${banco.id}`)}>
                    Ver extrato
                  </Button>
                  <Button size="sm" flex="1" variant="outline" colorScheme="teal" onClick={() => abrirAjuste(banco)}>
                    Ajustar saldo
                  </Button>
                </Flex>
              </Box>
            );
          })}
        </VStack>
      )}

      {/* Cadastro de banco */}
      <Modal isOpen={novo.isOpen} onClose={novo.onClose} isCentered>
        <ModalOverlay />
        <ModalContent mx={4} as="form" onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleAddBanco(); }}>
          <ModalHeader>Novo banco</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel htmlFor="nomeBanco">Nome</FormLabel>
                <Input id="nomeBanco" value={nomeBanco} onChange={(e) => setNomeBanco(e.target.value)} placeholder="Ex: Itaú, Nubank, Caixa" />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="saldoInicial">Saldo de hoje</FormLabel>
                <InputGroup>
                  <InputLeftAddon>R$</InputLeftAddon>
                  <Input id="saldoInicial" value={saldoInicial} onChange={handleSaldoInicialChange} placeholder="0,00" inputMode="decimal" textAlign="right" />
                </InputGroup>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={novo.onClose}>Cancelar</Button>
            <Button type="submit" colorScheme="teal" isLoading={loading}>Adicionar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Confirmação de exclusão */}
      <AlertDialog isOpen={!!excluirBanco} leastDestructiveRef={cancelExcluirRef} onClose={() => setExcluirBanco(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir {excluirBanco?.nome}?
            </AlertDialogHeader>
            <AlertDialogBody>
              Essa ação não pode ser desfeita. Bancos com lançamentos ou contas
              a pagar vinculados não podem ser excluídos — nesse caso, apague os
              lançamentos primeiro (ou mantenha o banco e só renomeie).
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelExcluirRef} onClick={() => setExcluirBanco(null)}>Cancelar</Button>
              <Button colorScheme="red" onClick={handleExcluirBanco} ml={3} isLoading={loading}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Diálogo de ajuste: renomear e acertar o saldo real */}
      <AlertDialog isOpen={!!ajusteBanco} leastDestructiveRef={cancelRef} onClose={() => setAjusteBanco(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Ajustar {ajusteBanco?.nome}
            </AlertDialogHeader>
            <AlertDialogBody>
              <FormControl mb={4}>
                <FormLabel>Nome</FormLabel>
                <Input value={ajusteNome} onChange={e => setAjusteNome(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Saldo real de hoje</FormLabel>
                <InputGroup>
                  <InputLeftAddon>R$</InputLeftAddon>
                  <Input
                    value={ajusteSaldo}
                    onChange={e => setAjusteSaldo(formatarMoedaInput(e.target.value))}
                    inputMode="decimal"
                    textAlign="right"
                  />
                </InputGroup>
              </FormControl>
              <Text fontSize="sm" color="gray.500" mt={3}>
                Digite o saldo que aparece no app do banco agora. O sistema acerta a
                diferença sem mexer nos seus lançamentos.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setAjusteBanco(null)}>Cancelar</Button>
              <Button colorScheme="teal" onClick={handleAjustarBanco} ml={3} isLoading={loading}>
                Salvar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Bancos;